import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { FormGuardOperations } from '../../ducks/formGuard';
import { Modal } from '@panel/commons';
import { SaveChangeForm } from '../modals/SaveChangeForm';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { sendModalTrackingInfo } from '../../utils/adaptStepDataAndSendTrackinInfo';
import { setCookieWithTTL } from '@panel/commons/helpers/cookieHelper';
import { isBrasilSite } from '@panel/commons/helpers';

const CustomPrompt = ({ when, isMobile, isEdition }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const history = useHistory();
  const currentLocation = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Leemos cookie para evitar mostrar el Prompt si ya se está haciendo la navegación desde el modal de guardar y salir
  const redirectFromSaveAndExitModalCookie = Cookies.get('redirectFromSaveAndExitModal');

  // Efecto cuando hay cambios en el formulario, se encarga de la navegación una vez confirmada
  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Reseteamos para futuras navegaciones
      setShowDialog(false);
      setConfirmedNavigation(false);
      // Navegamos a la ubicación deseada
      history.push(lastLocation.pathname);
      // si estoy saliendo del paso main por acciones del menu no muestro el snackbar
      if (!currentLocation.pathname.includes('/main') && !isEdition) {
        setCookieWithTTL('postingSavedInfoCrossPanel', 'professionalPubli.formGuardModal.saveAndExit.snackbar');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedNavigation, lastLocation, history]);

  // Efecto cuando no hay cambios en el formulario (when es false) y permitimos la navegación sin mostrar el modal previo
  useEffect(() => {
    if (!when && !redirectFromSaveAndExitModalCookie) {
      const unlisten = history.listen((nextLocation, action) => {
        if (
          (action === 'PUSH' || action === 'REPLACE') &&
          // Si navegamos dentro del publicador profesionales no mostramos el snackbar
          !nextLocation.pathname.includes('publicador-profesionales') &&
          // Si estoy saliendo del paso main por acciones del menu no muestro el snackbar
          !currentLocation.pathname.includes('/main') &&
          !isEdition
        ) {
          setCookieWithTTL('postingSavedInfoCrossPanel', 'professionalPubli.formGuardModal.saveAndExit.snackbar');
        }
      });
      return unlisten;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when, history, redirectFromSaveAndExitModalCookie]);

  useEffect(() => {
    if (showDialog) sendModalTrackingInfo('impression_modal', 'Modal salir');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog]);

  const handleBlockedNavigation = nextLocation => {
    // Si when es false, permitir la navegación
    if (!when) return true; // Permitir navegación
    // Si es la misma ubicación, permitir
    if (nextLocation.pathname === history.location.pathname) return true;
    // Si es dentro del publicador profesionales, permitir
    if (nextLocation.pathname.includes('publicador-profesionales')) return true;
    // Guardar la ubicación y mostrar el diálogo
    setLastLocation(nextLocation);
    setShowDialog(true);

    return false;
  };

  const handleConfirmNavigation = () => {
    if (showDialog) sendModalTrackingInfo('click_modal', 'Modal salir', 'Salir');
    dispatch(FormGuardOperations.setDirtyForm(false));
    setConfirmedNavigation(true);
  };

  const handleCancelNavigation = () => {
    if (showDialog) sendModalTrackingInfo('click_modal', 'Modal salir', 'Continuar editando');
    setShowDialog(false);
  };

  return (
    <>
      {/* Detenemos el Prompt si ya se está haciendo la navegación desde el modal de guardar y salir */}
      <Prompt when={!redirectFromSaveAndExitModalCookie && when} message={handleBlockedNavigation} />

      {showDialog && (
        <Modal>
          <SaveChangeForm
            bottomModal={isMobile}
            data-publi={`formGuardModal-container`}
            openModal={showDialog}
            onClose={handleCancelNavigation}
            handleRedirect={handleConfirmNavigation}
            handleKeepEditing={handleCancelNavigation}
            title={t('professionalPubli.formGuardModal.title')}
            description={t(`professionalPubli.formGuardModal.description${isBrasilSite ? '.s' : ''}`)}
            exitButtonText={t('professionalPubli.formGuardModal.exit')}
            keepButtonText={t('professionalPubli.formGuardModal.keep')}
          />
        </Modal>
      )}
    </>
  );
};

export default CustomPrompt;
